import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  FormControl,
  FormLabel,
  Input,
  LinearProgress,
  Modal,
  ModalDialog,
  Stack,
  Textarea,
  Typography,
} from "@mui/joy";
import { useUploadMedia } from "../../hooks/useMarketing";
import { mediaError, tagNames, type UploadResult } from "./mediaApi";

interface UploadRow {
  id: string;
  file: File;
  name: string;
  description: string;
  alt: string;
  tags: string;
  state: "ready" | "invalid" | "uploading" | "failed" | "done" | "duplicate";
  message?: string;
}
const mediaTypes =
  "image/jpeg,image/png,image/gif,image/webp,video/mp4,video/webm";
function FilePreview({ file }: { file: File }) {
  const [url, setUrl] = useState("");
  useEffect(() => {
    const next = URL.createObjectURL(file);
    setUrl(next);
    return () => URL.revokeObjectURL(next);
  }, [file]);
  return file.type.startsWith("image/") ? (
    <Box
      component="img"
      src={url}
      alt="Upload preview"
      sx={{ width: 80, height: 60, objectFit: "cover", borderRadius: "sm" }}
    />
  ) : (
    <Typography level="body-sm">
      {file.type.startsWith("video/") ? "Video" : "File"}
    </Typography>
  );
}
export function MediaUploadModal({
  open,
  onClose,
  acceptedTypes = ["image", "video"],
}: {
  open: boolean;
  onClose: () => void;
  acceptedTypes?: ("image" | "video")[];
}) {
  const accept = mediaTypes
    .split(",")
    .filter((type) => acceptedTypes.some((kind) => type.startsWith(`${kind}/`)))
    .join(",");
  const supported =
    acceptedTypes.length === 1
      ? acceptedTypes[0] === "image"
        ? "JPEG, PNG, GIF and WebP"
        : "MP4 and WebM"
      : "JPEG, PNG, GIF, WebP, MP4 and WebM";
  const [rows, setRows] = useState<UploadRow[]>([]);
  const [busy, setBusy] = useState(false);
  const upload = useUploadMedia();
  const update = (id: string, patch: Partial<UploadRow>) =>
    setRows((previous) =>
      previous.map((row) => (row.id === id ? { ...row, ...patch } : row)),
    );
  const add = (files: FileList | File[]) => {
    const added = Array.from(files).map((file) => {
      const message = !accept.split(",").includes(file.type)
        ? `Supported: ${supported}.`
        : !file.size || file.size > 10 * 1024 * 1024
          ? "Choose a nonempty file up to 10 MB."
          : undefined;
      return {
        id: crypto.randomUUID(),
        file,
        name: file.name,
        description: "",
        alt: "",
        tags: "",
        state: message ? ("invalid" as const) : ("ready" as const),
        message,
      };
    });
    setRows((previous) => [...previous, ...added]);
  };
  const pending = rows.filter(
    (row) => row.state === "ready" || row.state === "failed",
  );
  const complete = rows.filter(
    (row) => row.state === "done" || row.state === "duplicate",
  ).length;
  const run = async () => {
    if (busy) return;
    setBusy(true);
    for (const row of pending) {
      update(row.id, { state: "uploading", message: undefined });
      try {
        const result = (await upload.mutateAsync({
          files: [row.file],
          name: row.name,
          description: row.description,
          altText: row.alt,
          tags: tagNames(row.tags),
        })) as UploadResult;
        if (result.errors?.length)
          update(row.id, { state: "failed", message: result.errors[0].error });
        else if (result.duplicates?.length)
          update(row.id, {
            state: "duplicate",
            message: `Already in library: ${result.duplicates[0].name}. Existing metadata preserved.`,
          });
        else if (result.uploaded?.length)
          update(row.id, { state: "done", message: "Uploaded successfully." });
        else
          update(row.id, {
            state: "failed",
            message: "No upload result received. Retry this file.",
          });
      } catch (error) {
        update(row.id, { state: "failed", message: mediaError(error) });
      }
    }
    setBusy(false);
  };
  const close = () => {
    if (busy) return;
    if (
      pending.length &&
      !window.confirm(
        `Discard ${pending.length} unuploaded file${pending.length === 1 ? "" : "s"}?`,
      )
    )
      return;
    setRows([]);
    onClose();
  };
  return (
    <Modal open={open} onClose={close}>
      <ModalDialog
        sx={{
          width: 760,
          maxWidth: "94vw",
          maxHeight: "92vh",
          overflowY: "auto",
        }}
      >
        <Typography level="h4">
          {acceptedTypes.length === 1 && acceptedTypes[0] === "image"
            ? "Upload images"
            : "Upload media"}
        </Typography>
        <Typography level="body-sm">
          Review each file before uploading. {supported}, up to 10 MB each.
          Exact duplicates reuse the existing asset.
          {acceptedTypes.includes("video") &&
            " Video playback depends on your browser’s codec support."}
        </Typography>
        <Box
          onDragOver={(event) => event.preventDefault()}
          onDrop={(event) => {
            event.preventDefault();
            if (!busy) add(event.dataTransfer.files);
          }}
          sx={{
            p: 3,
            border: "2px dashed",
            borderColor: "neutral.outlinedBorder",
            borderRadius: "md",
          }}
        >
          <FormControl>
            <FormLabel>Drop files here or browse</FormLabel>
            <Input
              type="file"
              disabled={busy}
              slotProps={{
                input: {
                  multiple: true,
                  accept,
                  "aria-label": "Choose media files",
                },
              }}
              onChange={(event) => {
                if (event.target.files) add(event.target.files);
                event.target.value = "";
              }}
            />
          </FormControl>
        </Box>
        {rows.length > 0 && (
          <>
            <Typography level="title-sm" aria-live="polite">
              {complete} of {rows.length} files completed
            </Typography>
            <LinearProgress
              determinate
              value={(complete / rows.length) * 100}
            />
          </>
        )}
        <Stack spacing={2}>
          {rows.map((row) => (
            <Card key={row.id} variant="outlined">
              <Stack direction="row" spacing={2} alignItems="center">
                <FilePreview file={row.file} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ overflowWrap: "anywhere" }}>
                    {row.file.name}
                  </Typography>
                  <Typography level="body-xs">
                    {(row.file.size / 1024 / 1024).toFixed(2)} MB · {row.state}
                  </Typography>
                </Box>
                <Button
                  size="sm"
                  variant="plain"
                  disabled={busy}
                  onClick={() =>
                    setRows((previous) =>
                      previous.filter((item) => item.id !== row.id),
                    )
                  }
                >
                  Remove
                </Button>
              </Stack>
              {row.message && (
                <Alert
                  color={
                    row.state === "failed" || row.state === "invalid"
                      ? "danger"
                      : "success"
                  }
                >
                  {row.message}
                </Alert>
              )}
              {!["done", "duplicate", "invalid"].includes(row.state) && (
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                    gap: 1,
                  }}
                >
                  <FormControl>
                    <FormLabel>Name</FormLabel>
                    <Input
                      value={row.name}
                      disabled={busy}
                      onChange={(event) =>
                        update(row.id, { name: event.target.value })
                      }
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel>Tags, separated by commas</FormLabel>
                    <Input
                      value={row.tags}
                      disabled={busy}
                      onChange={(event) =>
                        update(row.id, { tags: event.target.value })
                      }
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel>Description</FormLabel>
                    <Textarea
                      value={row.description}
                      disabled={busy}
                      onChange={(event) =>
                        update(row.id, { description: event.target.value })
                      }
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel>Alt text</FormLabel>
                    <Input
                      value={row.alt}
                      disabled={busy || row.file.type.startsWith("video/")}
                      onChange={(event) =>
                        update(row.id, { alt: event.target.value })
                      }
                    />
                  </FormControl>
                </Box>
              )}
            </Card>
          ))}
        </Stack>
        <Stack direction="row" justifyContent="flex-end" spacing={1}>
          <Button variant="plain" disabled={busy} onClick={close}>
            Done
          </Button>
          <Button
            loading={busy}
            disabled={
              !pending.length || pending.some((row) => !row.name.trim())
            }
            onClick={run}
          >
            {rows.some((row) => row.state === "failed")
              ? "Retry remaining files"
              : `Upload ${pending.length} files`}
          </Button>
        </Stack>
      </ModalDialog>
    </Modal>
  );
}
